import React from 'react';
import { IonPage, IonContent } from '@ionic/react';
import './StatsPage.css';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { figures } from '../../spinnaxBase/data/figures';

// Farben aus CSS-Variablen holen
const getColor = (cssVar: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(cssVar) ||
  '#3880ff';

const PRIMARY = getColor('--ion-color-primary') || '#3880ff';
const SECONDARY = getColor('--ion-color-secondary') || '#3dc2ff';

// Mockdaten
const figuresStats = [
  { name: 'Gestanden', value: 24 },
  { name: 'Nicht gestanden', value: 8 },
];

// Erzeuge Mockdaten für alle Figures mit gestanden/nicht gestanden
const figureClasses = figures.map((fig, i) => ({
  name: fig.label,
  achieved: Math.floor(Math.random() * 8) + 2, // Zufallswert für gestanden
  unachieved: Math.floor(Math.random() * 4) + 1, // Zufallswert für nicht gestanden
}));

const statsValues = [
  { label: 'Gesamte Strecke (km)', value: 12.7 },
  { label: 'Gesamte Sprünge', value: 37 },
  { label: 'Anzahl Runs', value: 52 },
  { label: 'Gesamte Slides', value: 19 },
  { label: 'Max. Rotationen/Min', value: 38 },
  { label: 'Höchster Sprung (m)', value: 0.82 },
  { label: 'Durchschnittliche Geschwindigkeit', value: 14.2 },
  { label: 'Max. Airtime (s)', value: 1.8 },
  { label: 'Max. Slidezeit (s)', value: 2.3 },
  { label: 'Max. Punkte in einem Run', value: 4200 },
  { label: 'Längster Manual (s)', value: 7.4 },
  { label: 'Max. Manualzeit (s)', value: 3.1 },
];

// Mockdaten für Spins vs Flips
const spinLabels = [
  'FRONTSIDE 180',
  'BACKSIDE 180',
  'FS POP SHOVE IT',
  'BS POP SHOVE IT',
  '360 FLIP',
  'TRE FLIP',
];
const flipLabels = [
  'KICKFLIP',
  'HEELFLIP',
  'VARIAL KICKFLIP',
  'VARIAL HEELFLIP',
  'HARDFLIP',
  'INWARD HEELFLIP',
];
const spinsCount = figureClasses
  .filter((f) => spinLabels.includes(f.name.toUpperCase()))
  .reduce((a, b) => a + b.achieved + b.unachieved, 0);
const flipsCount = figureClasses
  .filter((f) => flipLabels.includes(f.name.toUpperCase()))
  .reduce((a, b) => a + b.achieved + b.unachieved, 0);
const spinsFlipsData = [
  { name: 'Spins', value: spinsCount },
  { name: 'Flips', value: flipsCount },
];
// Mockdaten für Score über Zeit
const months = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
];
const scoreOverTime = months.map((month, i) => ({
  month,
  value: Math.round(1000 + 200 * i + Math.random() * 100),
}));
const figuresPerMonth = months.map((month, i) => ({
  month,
  value: Math.round(10 + 8 * Math.sin(i / 2) + Math.random() * 3),
}));
const avgScorePerFigure = figureClasses.map((f) => ({
  name: f.name,
  value: Math.round(500 + Math.random() * 1000),
}));
const bestScorePerFigure = figureClasses.map((f) => ({
  name: f.name,
  value: Math.round(1000 + Math.random() * 2000),
}));
const avgScorePerMonth = months.map((month, i) => ({
  month,
  value: Math.round(800 + 100 * Math.sin(i / 2) + Math.random() * 80),
}));
const bestScorePerMonth = months.map((month, i) => ({
  month,
  value: Math.round(1500 + 200 * Math.cos(i / 3) + Math.random() * 120),
}));

const COLORS = [PRIMARY, SECONDARY];
const BAR_COLORS = [PRIMARY, SECONDARY, PRIMARY, SECONDARY, PRIMARY];

// Pie-Label als eigene Komponente für immer weiße Schrift
const PieLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  name,
  value,
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontWeight="bold"
      fontSize={16}
    >
      {name}: {value}
    </text>
  );
};

const StatsPage: React.FC = () => (
  <IonPage>
    <IonContent fullscreen className="stats-content">
      <h2 className="stats-title">Deine Stats</h2>
      <h2 className="stats-title" style={{ color: 'orange' }}>Service unavailable using proxy data</h2>
      <div className="stats-charts-container">
        <div className="stats-charts-row">
          <div className="stats-chart-block stats-chart-block-wide stats-chart-block-double">
            <h3>Figures nach Klasse</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={figureClasses}>
                <XAxis
                  dataKey="name"
                  allowDecimals={false}
                  tick={{ fill: '#fff', fontWeight: 'bold', fontSize: 13 }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: '#fff', fontWeight: 'bold', fontSize: 13 }}
                />
                <Tooltip
                  contentStyle={{
                    background: '#222',
                    color: '#fff',
                    border: 'none',
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar
                  dataKey="achieved"
                  stackId="a"
                  fill={PRIMARY}
                  name="Gestanden"
                />
                <Bar
                  dataKey="unachieved"
                  stackId="a"
                  fill={SECONDARY}
                  name="Nicht gestanden"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="stats-charts-row">
          <div className="stats-chart-block stats-chart-block-wide">
            <h3>Gestandene vs. nicht gestandene Figures</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={figuresStats}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={PieLabel}
                >
                  {figuresStats.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: '#222',
                    color: '#fff',
                    border: 'none',
                  }}
                  labelStyle={{ color: '#fff' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="stats-chart-block stats-chart-block-wide">
            <h3>Spins vs. Flips</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={spinsFlipsData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={PieLabel}
                >
                  {spinsFlipsData.map((entry, index) => (
                    <Cell
                      key={`cell-spinflip-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: '#222',
                    color: '#fff',
                    border: 'none',
                  }}
                  labelStyle={{ color: '#fff' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="stats-charts-row">
          <div className="stats-chart-block stats-chart-block-wide">
            <h3>Total Score über Zeit</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={scoreOverTime} margin={{ left: 10, right: 10 }}>
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#fff', fontWeight: 'bold' }}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: '#fff', fontWeight: 'bold' }}
                />
                <Tooltip
                  contentStyle={{
                    background: '#222',
                    color: '#fff',
                    border: 'none',
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={SECONDARY}
                  strokeWidth={3}
                  dot={{ r: 5, fill: PRIMARY }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="stats-chart-block stats-chart-block-wide">
            <h3>Figures pro Monat (letztes Jahr, geglättet)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={figuresPerMonth}
                margin={{ left: 10, right: 10 }}
              >
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#fff', fontWeight: 'bold' }}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: '#fff', fontWeight: 'bold' }}
                />
                <Tooltip
                  contentStyle={{
                    background: '#222',
                    color: '#fff',
                    border: 'none',
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={PRIMARY}
                  strokeWidth={3}
                  dot={{ r: 5, fill: SECONDARY }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="stats-charts-row">
          <div className="stats-chart-block stats-chart-block-wide">
            <h3>Ø Score pro Monat</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={avgScorePerMonth}
                margin={{ left: 10, right: 10 }}
              >
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#fff', fontWeight: 'bold' }}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: '#fff', fontWeight: 'bold' }}
                />
                <Tooltip
                  contentStyle={{
                    background: '#222',
                    color: '#fff',
                    border: 'none',
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={SECONDARY}
                  strokeWidth={3}
                  dot={{ r: 5, fill: PRIMARY }}
                  activeDot={{ r: 7 }}
                  name="Ø Score"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="stats-chart-block stats-chart-block-wide">
            <h3>Bester Score pro Monat</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={bestScorePerMonth}
                margin={{ left: 10, right: 10 }}
              >
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#fff', fontWeight: 'bold' }}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: '#fff', fontWeight: 'bold' }}
                />
                <Tooltip
                  contentStyle={{
                    background: '#222',
                    color: '#fff',
                    border: 'none',
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={PRIMARY}
                  strokeWidth={3}
                  dot={{ r: 5, fill: SECONDARY }}
                  activeDot={{ r: 7 }}
                  name="Bestwert"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="stats-values-block stats-values-row">
          <h3>Bestwerte</h3>
          <ul className="stats-values-list">
            {statsValues.map((item, idx) => (
              <li key={idx} className="stats-value-item stats-value-item-large">
                <span className="stats-value-label">{item.label}:</span>
                <span className="stats-value-number">{item.value}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </IonContent>
  </IonPage>
);

export default StatsPage;
